import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Save, CheckCircle } from 'lucide-react';

const DailyLog = () => {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leadsAdded, setLeadsAdded] = useState(0);
  const [form, setForm] = useState({
    connection_requests_sent: 0, connections_accepted: 0, replies_received: 0,
    qualified_leads: 0, handoffs_to_prateek: 0, calls_booked: 0, proposals_sent: 0,
    generic_messages_sent: 0, personalized_messages_sent: 0,
    handoff_type_a: 0, handoff_type_b_reveal: 0, handoff_type_b_confirm: 0,
    handoff_type_b_no_reply: 0, notes: ''
  });

  useEffect(() => {
    const loadExisting = async () => {
      try {
        const [statsRes, leadsRes] = await Promise.all([
          api.get(`/stats/my?from=${date}&to=${date}`),
          api.get(`/leads?from=${date}&to=${date}`)
        ]);

        // Set leads count for the selected date. Use the server's `total`
        // (full count for the date) rather than the returned rows, which are
        // now a single paginated page and would under-count a busy day.
        setLeadsAdded(leadsRes.data?.total ?? (leadsRes.data?.leads || []).length);

        if (statsRes.data.stats?.length) {
          const s = statsRes.data.stats[0];
          setForm({
            connection_requests_sent: s.connection_requests_sent || 0,
            connections_accepted: s.connections_accepted || 0,
            replies_received: s.replies_received || 0,
            qualified_leads: s.qualified_leads || 0,
            handoffs_to_prateek: s.handoffs_to_prateek || 0,
            calls_booked: s.calls_booked || 0,
            proposals_sent: s.proposals_sent || 0,
            generic_messages_sent: s.generic_messages_sent || 0,
            personalized_messages_sent: s.personalized_messages_sent || 0,
            handoff_type_a: s.handoff_type_a || 0,
            handoff_type_b_reveal: s.handoff_type_b_reveal || 0,
            handoff_type_b_confirm: s.handoff_type_b_confirm || 0,
            handoff_type_b_no_reply: s.handoff_type_b_no_reply || 0,
            notes: s.notes || ''
          });
          setSaved(true);
        } else {
          setForm({
            connection_requests_sent: 0, connections_accepted: 0, replies_received: 0,
            qualified_leads: 0, handoffs_to_prateek: 0, calls_booked: 0, proposals_sent: 0,
            generic_messages_sent: 0, personalized_messages_sent: 0,
            handoff_type_a: 0, handoff_type_b_reveal: 0, handoff_type_b_confirm: 0,
            handoff_type_b_no_reply: 0, notes: ''
          });
          setSaved(false);
        }
      } catch (err) { console.error(err); }
    };
    loadExisting();
  }, [date]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: field === 'notes' ? value : parseInt(value) || 0 }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/stats/daily', { ...form, stat_date: date });
      setSaved(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  const NumInput = ({ label, field }) => (
    <div className="form-group">
      <label>{label}</label>
      <input type="number" min="0" value={form[field]}
        onChange={e => handleChange(field, e.target.value)} />
    </div>
  );

  // Read-only display showing the auto-counted leads added on the selected date.
  // Number comes from the leads table, not from manual entry.
  const ReadOnlyLeadsAdded = () => (
    <div className="form-group">
      <label>Leads Added Today</label>
      <input
        type="number"
        value={leadsAdded}
        readOnly
        disabled
        style={{ background: '#F3F4F6', cursor: 'not-allowed' }}
      />
      <p style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
        Auto-counted from the Leads page. Add leads there.
      </p>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2>Daily Stats Log</h2>
        <p>Log your outreach numbers for the day</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>
            {saved && <CheckCircle size={18} color="#2E7D32" style={{ marginRight: 8 }} />}
            {saved ? 'Stats saved' : 'Enter stats'}
          </h3>
          <div className="form-group" style={{ marginBottom: 0, width: 180 }}>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} max={today} />
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <h4 style={{ color: '#1F3A5F', marginBottom: 12, fontSize: 14 }}>Outreach Metrics</h4>
          <div className="form-row">
            <NumInput label="Connection Requests Sent" field="connection_requests_sent" />
            <NumInput label="Connections Accepted" field="connections_accepted" />
            <NumInput label="Replies Received" field="replies_received" />
            <ReadOnlyLeadsAdded />
          </div>
          <div className="form-row">
            <NumInput label="Handoffs to Prateek" field="handoffs_to_prateek" />
            <NumInput label="Calls Booked" field="calls_booked" />
            <NumInput label="Proposals Sent" field="proposals_sent" />
          </div>

          <h4 style={{ color: '#1F3A5F', margin: '24px 0 12px', fontSize: 14 }}>Message Split (V2 Test)</h4>
          <div className="form-row">
            <NumInput label="Generic Messages Sent" field="generic_messages_sent" />
            <NumInput label="Personalized Messages Sent" field="personalized_messages_sent" />
          </div>

          <h4 style={{ color: '#1F3A5F', margin: '24px 0 12px', fontSize: 14 }}>Handoff Type Breakdown</h4>
          <div className="form-row">
            <NumInput label="Type A (Project Named)" field="handoff_type_a" />
            <NumInput label="B-Reveal (Revealed After 2nd Msg)" field="handoff_type_b_reveal" />
            <NumInput label="B-Confirm (Exploratory Close)" field="handoff_type_b_confirm" />
            <NumInput label="B-No Reply (Silent Close)" field="handoff_type_b_no_reply" />
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label>Notes (optional)</label>
            <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)}
              placeholder="Anything notable today — a great reply, a pattern, a struggle..." />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            <Save size={16} /> {loading ? 'Saving...' : saved ? 'Update' : 'Save Daily Log'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DailyLog;
